import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './SuccessToast.module.css';

export const SUCCESS_TOAST_DURATION_MS = 8000;
export const SUCCESS_TOAST_EXIT_LEAD_MS = 350;

export function SuccessToast({ message, isExiting }) {
  const gradientId = useId();

  if (!message || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={`${styles.successToast}${isExiting ? ` ${styles.successToastExiting}` : ''}`}
      role="status"
      aria-live="polite"
    >
      <span className={styles.successToastIcon} aria-hidden="true">
        <svg viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="22" y2="22" gradientUnits="userSpaceOnUse">
              <stop stopColor="#1aaa77" />
              <stop offset="1" stopColor="#0b8a7a" />
            </linearGradient>
          </defs>
          <circle cx="11" cy="11" r="11" fill={`url(#${gradientId})`} />
          <path
            d="M6 11.5l3.5 3.5 6.5-6.5"
            stroke="#fff"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span className={styles.successToastMsg}>{message}</span>
      <span
        className={styles.successToastBar}
        style={{ '--toast-duration': `${SUCCESS_TOAST_DURATION_MS}ms` }}
        aria-hidden="true"
      />
    </div>,
    document.body,
  );
}

export function useSuccessToast() {
  const [message, setMessage] = useState('');
  const [isExiting, setIsExiting] = useState(false);
  const exitTimerRef = useRef(null);
  const dismissTimerRef = useRef(null);

  useEffect(() => () => {
    clearTimeout(exitTimerRef.current);
    clearTimeout(dismissTimerRef.current);
  }, []);

  const dismissToast = useCallback(() => {
    clearTimeout(exitTimerRef.current);
    clearTimeout(dismissTimerRef.current);
    setMessage('');
    setIsExiting(false);
  }, []);

  const showToast = useCallback((text) => {
    if (!text) {
      dismissToast();
      return;
    }

    setMessage(text);
    setIsExiting(false);
    clearTimeout(exitTimerRef.current);
    clearTimeout(dismissTimerRef.current);
    exitTimerRef.current = setTimeout(
      () => setIsExiting(true),
      SUCCESS_TOAST_DURATION_MS - SUCCESS_TOAST_EXIT_LEAD_MS,
    );
    dismissTimerRef.current = setTimeout(() => {
      setMessage('');
      setIsExiting(false);
    }, SUCCESS_TOAST_DURATION_MS);
  }, [dismissToast]);

  return { message, isExiting, showToast, dismissToast };
}
